import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  CredentialEntry,
  CredentialType,
  encryptCredential,
  encryptWithPublicKey,
  validatePasswordStrength
} from "../../utils/crypto/vault-crypto";
import axios from "../../utils/api/axios";
import { FiEye, FiEyeOff } from "react-icons/fi";
import {
  credentialTypes,
  entryTypes,
  getLabelPlaceholder,
  getDefaultEntries
} from "./credential-utils";
// import PasswordGenerator from "./PasswordGenerator";

interface Target {
  target_id: string;
  target_name: string;
  target_value: string;
  asset?: {
    name: string;
  };
}

interface CredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    label: string;
    credential_type: CredentialType;
    url?: string;
    username?: string;
    encrypted_data: string;
    iv: string;
    auth_tag: string;
    salt?: string;
    key_iterations?: number;
    expires_at?: string;
    rotation_reminder_days?: number;
    strength_score?: number;
    is_favorite?: boolean;
    notes?: string;
    // For asymmetric encryption (sub-user mode)
    encrypted_credential_key?: string;
  }) => Promise<void>;
  initialData?: {
    label: string;
    credential_type?: CredentialType;
    url?: string;
    username?: string;
    expires_at?: string;
    rotation_reminder_days?: number;
    is_favorite?: boolean;
    notes?: string;
    entries: CredentialEntry[];
  };
  // Either masterPassword OR publicKey should be provided
  // masterPassword = owner mode (symmetric encryption)
  // publicKey = sub-user mode (asymmetric encryption, write-only)
  masterPassword?: string;
  publicKey?: string;
  isEditing?: boolean;
}



const CredentialModal: React.FC<CredentialModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  masterPassword,
  publicKey,
  isEditing = false
}) => {
  // Determine encryption mode: owner (master password) or sub-user (public key)
  const isSubUserMode = !masterPassword && !!publicKey;
  const modalRef = useRef<HTMLDivElement>(null);
  const [label, setLabel] = useState("");
  const [credentialType, setCredentialType] = useState<CredentialType>("password");
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [notes, setNotes] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [rotationReminderDays, setRotationReminderDays] = useState<number>(30);
  const [isFavorite, setIsFavorite] = useState(false);
  const [entries, setEntries] = useState<CredentialEntry[]>([
    { key: "", value: "", type: "text" }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState<"basic" | "fields" | "advanced">("basic");
  const [showValues, setShowValues] = useState<Record<number, boolean>>({});
  const [companyTargets, setCompanyTargets] = useState<Target[]>([]);
  const [isSearchingTargets, setIsSearchingTargets] = useState(false);
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);

  // Reset tab to basic only when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic");
      setError(null);
      fetchCompanyTargets();
    }
  }, [isOpen]);

  const fetchCompanyTargets = async () => {
    setIsSearchingTargets(true);
    try {
      const response = await axios.get("/assets/all-targets");
      setCompanyTargets(response.data);
    } catch (err) {
      console.error("Failed to fetch targets:", err);
    } finally {
      setIsSearchingTargets(false);
    }
  };

  // Populate form data when initialData changes (only process once when modal opens)
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (initialData) {
        setLabel(initialData.label);
        setCredentialType(initialData.credential_type || "password");
        setUrl(initialData.url || "");
        setUsername(initialData.username || "");
        setNotes(initialData.notes || "");
        setExpiresAt(initialData.expires_at ? initialData.expires_at.split("T")[0] : "");
        setRotationReminderDays(initialData.rotation_reminder_days || 30);
        setIsFavorite(initialData.is_favorite || false);
        setEntries(initialData.entries.length > 0 ? initialData.entries : getDefaultEntries(initialData.credential_type || "password"));
      } else {
        setLabel("");
        setCredentialType("password");
        setUrl("");
        setUsername("");
        setNotes("");
        setExpiresAt("");
        setRotationReminderDays(30);
        setIsFavorite(false);
        setEntries(getDefaultEntries("password"));
      }
      setHasInitialized(true);
    }
    // Reset hasInitialized and entries when modal closes
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen, initialData, hasInitialized]);

  // Auto-populate entries based on credential type for new credentials when type is changed by user
  useEffect(() => {
    if (isOpen && !isEditing && hasInitialized) {
      setEntries(getDefaultEntries(credentialType));
    }
  }, [credentialType]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
      // Close target dropdown if clicking outside its area
      const targetInput = document.querySelector('[data-target-dropdown]');
      if (targetInput && !targetInput.contains(e.target as Node)) {
        setShowTargetDropdown(false);
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const addEntry = () => {
    setEntries([...entries, { key: "", value: "", type: "text" }]);
  };

  const removeEntry = (index: number) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const updateEntry = (index: number, field: keyof CredentialEntry, value: string) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const toggleShowValue = (index: number) => {
    setShowValues(prev => ({ ...prev, [index]: !prev[index] }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!label.trim()) {
      setError("Label is required");
      return;
    }

    // If on basic tab, move to fields tab instead of submitting
    if (activeTab === "basic") {
      setActiveTab("fields");
      return;
    }

    const validEntries = entries.filter((e) => e.key.trim() && e.value.trim());
    if (validEntries.length === 0 && credentialType !== "secure_note") {
      setError("At least one credential entry is required");
      return;
    }

    // Validate encryption mode - need either master password or public key
    if (!masterPassword && !publicKey) {
      setError("Encryption key required");
      return;
    }

    // For owner mode, validate master password strength
    if (masterPassword) {
      const validation = validatePasswordStrength(masterPassword);
      if (!validation.isValid) {
        setError(`Master password is too weak: ${validation.feedback.join(", ")}`);
        return;
      }
    }

    setIsLoading(true);
    try {
      let encryptedData: {
        encrypted_data: string;
        iv: string;
        auth_tag: string;
        salt?: string;
        key_iterations?: number;
        encrypted_credential_key?: string;
      };

      if (isSubUserMode && publicKey) {
        // Sub-user mode: Use asymmetric encryption (RSA + AES)
        // This allows sub-users to add credentials without knowing the master password
        // Only the vault owner can decrypt these credentials
        const hybridEncrypted = await encryptWithPublicKey(
          { entries: validEntries },
          publicKey
        );
        encryptedData = {
          encrypted_data: hybridEncrypted.encrypted_data,
          iv: hybridEncrypted.iv,
          auth_tag: hybridEncrypted.auth_tag,
          encrypted_credential_key: hybridEncrypted.encrypted_aes_key
        };
      } else if (masterPassword) {
        // Owner mode: Use symmetric encryption with master password
        const symmetric = await encryptCredential(
          { entries: validEntries },
          masterPassword
        );
        encryptedData = symmetric;
      } else {
        throw new Error("No encryption key available");
      }

      await onSave({
        label: label.trim(),
        credential_type: credentialType,
        url: url.trim() || undefined,
        username: username.trim() || undefined,
        notes: notes.trim() || undefined,
        expires_at: expiresAt || undefined,
        rotation_reminder_days: expiresAt ? rotationReminderDays : undefined,
        is_favorite: isFavorite,
        ...encryptedData
      });

      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to encrypt credentials");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div ref={modalRef} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        {/* Dark Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-slate-800 px-6 py-4 rounded-t-2xl">
          <h3 className="text-lg font-medium text-white">
            {isEditing ? "Edit Credential" : "Add New Credential"}
          </h3>
          <button onClick={onClose} className="rounded-md text-gray-400 hover:text-white">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tab Progress / Steps */}
          <div className="sticky top-[60px] z-10 flex border-b border-gray-200 px-6 bg-white">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setActiveTab("basic"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-all ${activeTab === "basic" ? "border-green-500 text-green-600" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${activeTab === "basic" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>1</span>
              Basic Info
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setActiveTab("fields"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-all ${activeTab === "fields" ? "border-green-500 text-green-600" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${activeTab === "fields" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>2</span>
              Credentials
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setActiveTab("advanced"); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-all ${activeTab === "advanced" ? "border-green-500 text-green-600" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${activeTab === "advanced" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>3</span>
              Advanced
            </button>
          </div>

          <div className="px-6 py-4 flex-1 overflow-y-auto">
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-5">
                {/* Credential Type */}
                <div>
                  <label className="mb-3 block text-sm font-semibold text-slate-800">
                    Credential Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {credentialTypes.slice(0, 6).map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setCredentialType(type.value)}
                        className={`relative flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                          credentialType === type.value
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {credentialType === type.value && (
                          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-500"></span>
                        )}
                        <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-white text-lg">
                          {type.icon}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800">{type.label}</div>
                          <div className="text-xs text-gray-500 leading-tight">{type.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium"
                  >
                    {showAdvanced ? "Hide" : "Show more"} types...
                  </button>
                  {showAdvanced && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      {credentialTypes.slice(6).map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setCredentialType(type.value)}
                          className={`relative flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                            credentialType === type.value
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {credentialType === type.value && (
                            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-500"></span>
                          )}
                          <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-white text-lg">
                            {type.icon}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-800">{type.label}</div>
                            <div className="text-xs text-gray-500 leading-tight">{type.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Label */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-800">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={getLabelPlaceholder(credentialType)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 placeholder-gray-400 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>

                {/* URL (Moved above for better flow) */}
                {credentialType !== "secure_note" && (
                  <div data-target-dropdown>
                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                      URL / Service
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                          setShowTargetDropdown(true);
                        }}
                        onFocus={() => setShowTargetDropdown(true)}
                        placeholder="https://example.com or select a target"
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 placeholder-gray-400 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                      {showTargetDropdown && companyTargets.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                          {companyTargets
                            .filter(t => 
                              t.target_name.toLowerCase().includes(url.toLowerCase()) || 
                              t.target_value.toLowerCase().includes(url.toLowerCase())
                            )
                            .map((t) => (
                              <button
                                key={t.target_id}
                                type="button"
                                onClick={() => {
                                  setUrl(t.target_value);
                                  setShowTargetDropdown(false);
                                  if (!label) setLabel(t.target_name);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-gray-100 last:border-0"
                              >
                                <div className="text-sm font-medium text-slate-800">{t.target_name}</div>
                                <div className="text-xs text-gray-500 truncate">{t.target_value}</div>
                                {t.asset && (
                                  <div className="text-[10px] text-teal-600 font-medium">Asset: {t.asset.name}</div>
                                )}
                              </button>
                            ))}
                          {companyTargets.filter(t => 
                            t.target_name.toLowerCase().includes(url.toLowerCase()) || 
                            t.target_value.toLowerCase().includes(url.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-xs text-gray-500">No matching targets found. You can still enter a custom URL.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Favorite Toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isFavorite}
                    onChange={(e) => setIsFavorite(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-slate-700">⭐ Mark as favourite</span>
                </label>
              </div>
            )}

            {/* Credential Fields Tab */}
            {activeTab === "fields" && (
              <div className="space-y-5">
                {/* Section Header */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">
                    {credentialType === "secure_note" ? "Secure Note" : "Credential Fields"}
                  </span>
                </div>

                {/* Secure Note */}
                {credentialType === "secure_note" ? (
                  <div>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Enter your secure note..."
                      rows={8}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 placeholder-gray-400 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                ) : (
                  <>
                    {/* Credential Entries */}
                    <div className="space-y-3">
                      {entries.map((entry, index) => (
                        <div key={index} className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <select
                            value={entry.type}
                            onChange={(e) => updateEntry(index, "type", e.target.value)}
                            className="w-28 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm text-slate-800 focus:border-green-500 focus:outline-none"
                          >
                            {entryTypes.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.icon} {t.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={entry.key}
                            onChange={(e) => updateEntry(index, "key", e.target.value)}
                            placeholder="Field name"
                            className="w-32 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-gray-400 focus:border-green-500 focus:outline-none"
                          />
<div className="relative flex-1">
                            <input
                              type={entry.type === "password" && !showValues[index] ? "password" : "text"}
                              value={entry.value}
                              onChange={(e) => updateEntry(index, "value", e.target.value)}
                              placeholder="Value"
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-sm text-slate-800 placeholder-gray-400 focus:border-green-500 focus:outline-none"
                            />
                            {entry.type === "password" && (
                              <button
                                type="button"
                                onClick={() => toggleShowValue(index)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600"
                                title={showValues[index] ? "Hide password" : "Show password"}
                              >
                                {showValues[index] ? <FiEyeOff /> : <FiEye />}
                              </button>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeEntry(index)}
                            disabled={entries.length === 1}
                            className="rounded-md px-2 text-gray-400 hover:text-red-500 disabled:opacity-30"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addEntry}
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      + Add another field
                    </button>
                  </>
                )}

                {/* Notes (for non-secure-note types) */}
                {credentialType !== "secure_note" && (
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any additional notes..."
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 placeholder-gray-400 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === "advanced" && (
              <div className="space-y-5">
                {/* Expiration Date */}
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-800">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Set when this credential should expire
                  </p>
                </div>

                {/* Rotation Reminder */}
                {expiresAt && (
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                      Reminder (days before expiry)
                    </label>
                    <select
                      value={rotationReminderDays}
                      onChange={(e) => setRotationReminderDays(parseInt(e.target.value))}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-slate-800 focus:border-green-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                )}

                {/* Security Notice */}
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  <strong>🔒 Zero-Knowledge Encryption:</strong> Your credentials are encrypted
                  client-side before being sent to the server. We never see your plaintext data.
                </div>

              </div>
            )}
          </div>

          {/* Actions - Teal/Cyan footer bar */}
          <div className="flex justify-between border-t border-gray-200 px-6 py-4 bg-teal-50 rounded-b-2xl">
            <div>
              {activeTab !== "basic" && (
                <button
                  type="button"
                  onClick={() => setActiveTab(activeTab === "advanced" ? "fields" : "basic")}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
              {activeTab === "basic" ? (
                <button
                  type="button"
                  onClick={() => {
                    if (label.trim()) {
                      setActiveTab("fields");
                      setError(null);
                    } else {
                      setError("Label is required before continuing");
                    }
                  }}
                  className="rounded-lg bg-green-500 px-8 py-2.5 text-sm font-medium text-white hover:bg-green-600 transition-colors shadow-sm"
                >
                  Continue
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="rounded-lg bg-green-500 px-8 py-2.5 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {isLoading ? "Encrypting..." : isEditing ? "Update" : "Save Credential"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CredentialModal;

